// apps/web/src/components/checklist/ChecklistItem.tsx
import { useState, useEffect } from 'react';
import type { ChecklistItem as ChecklistItemType, ChecklistStatus } from '@homework-app/shared';
import { ReadingTimer } from './ReadingTimer';

interface Props {
  item: ChecklistItemType;
  onStatusChange: () => void;
  apiService: {
    updateChecklistStatus(itemId: string, status: ChecklistStatus, comment?: string): Promise<void>;
    uploadChecklistPhoto(itemId: string, photo: File, phase: string): Promise<{ id: string; url: string }>;
    completeTimer(itemId: string): Promise<void>;
    completeHonor(itemId: string): Promise<void>;
  };
}

const STATUS_LABELS: Record<ChecklistStatus, string> = {
  not_started: 'Not Started',
  in_progress: 'In Progress',
  pending_ai: 'Checking...',
  pending_parent: 'Waiting for Dad',
  approved: 'Done!',
  returned: 'Try Again'
};

const STATUS_COLORS: Record<ChecklistStatus, string> = {
  not_started: '#6b7280',
  in_progress: '#3b82f6',
  pending_ai: '#f59e0b',
  pending_parent: '#8b5cf6',
  approved: '#10b981',
  returned: '#ef4444'
};

export function ChecklistItem({ item, onStatusChange, apiService }: Props) {
  const [uploading, setUploading] = useState(false);
  const [timerActive, setTimerActive] = useState(false);

  // Poll for status updates when AI verification is in progress
  useEffect(() => {
    if (item.status !== 'pending_ai') return;

    const pollInterval = setInterval(() => {
      onStatusChange(); // Re-fetch the checklist
    }, 3000); // Poll every 3 seconds

    return () => clearInterval(pollInterval);
  }, [item.status, onStatusChange]);

  const handleStart = async () => {
    try {
      await apiService.updateChecklistStatus(item.id, 'in_progress');
      onStatusChange();
    } catch (err) {
      console.error('Failed to start item:', err);
    }
  };

  const handlePhotoUpload = async (phase: 'before' | 'after') => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment';

    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      try {
        setUploading(true);
        await apiService.uploadChecklistPhoto(item.id, file, phase);

        // If this is an 'after' photo, move to pending_ai
        if (phase === 'after') {
          await apiService.updateChecklistStatus(item.id, 'pending_ai');
        }

        onStatusChange();
      } catch (err) {
        console.error('Failed to upload photo:', err);
      } finally {
        setUploading(false);
      }
    };

    input.click();
  };

  const handleTimerComplete = async () => {
    try {
      await apiService.completeTimer(item.id);
      setTimerActive(false);
      onStatusChange();
    } catch (err) {
      console.error('Failed to complete timer:', err);
    }
  };

  const handleHonorComplete = async () => {
    try {
      await apiService.completeHonor(item.id);
      onStatusChange();
    } catch (err) {
      console.error('Failed to complete honor item:', err);
    }
  };

  const handleResubmit = async () => {
    try {
      await apiService.updateChecklistStatus(item.id, 'in_progress');
      onStatusChange();
    } catch (err) {
      console.error('Failed to resubmit:', err);
    }
  };

  const beforePhotos = (item.photos || []).filter(p => p.phase === 'before');
  const afterPhotos = (item.photos || []).filter(p => p.phase === 'after');
  const timerMinutes = (item.config as Record<string, number>)?.timer_minutes || 15;

  return (
    <div
      className="checklist-item"
      style={{ borderLeftColor: STATUS_COLORS[item.status] }}
    >
      <div className="checklist-item-header">
        <h3>{item.title}</h3>
        <span
          className="checklist-item-status"
          style={{ backgroundColor: STATUS_COLORS[item.status] }}
        >
          {STATUS_LABELS[item.status]}
        </span>
      </div>

      {/* AI verification feedback */}
      {item.verification && (
        <div className={`checklist-item-feedback ${item.verification.passed ? 'passed' : 'failed'}`}>
          <div className="feedback-header">
            <span className="feedback-label">
              {item.verification.passed ? 'MARVIN says: Looks good' : 'MARVIN says: Not quite'}
            </span>
          </div>
          <p className="feedback-text">{item.verification.feedback}</p>
        </div>
      )}

      {/* Returned with parent comment when no verification result (manual return or AI error) */}
      {item.status === 'returned' && item.parent_comment && !item.verification && (
        <div className="checklist-item-feedback returned">
          <strong>Feedback:</strong> {item.parent_comment}
        </div>
      )}

      {/* Action area based on item type and status */}
      <div className="checklist-item-actions">
        {/* Not started: show start button */}
        {item.status === 'not_started' && item.verification_type === 'timer' && (
          <button onClick={handleStart} className="btn-start">
            Start Reading Timer ({timerMinutes} min)
          </button>
        )}

        {item.status === 'not_started' && item.verification_type === 'honor' && (
          <button onClick={handleHonorComplete} className="btn-honor">
            Mark as Done (Honor System)
          </button>
        )}

        {item.status === 'not_started' && item.verification_type === 'ai_photo' && (
          <button
            onClick={() => handlePhotoUpload('before')}
            disabled={uploading}
            className="btn-photo"
          >
            {uploading ? 'Uploading...' : 'Take Before Photo'}
          </button>
        )}

        {/* In progress: show appropriate next action */}
        {item.status === 'in_progress' && item.verification_type === 'ai_photo' && (
          <div className="photo-actions">
            {beforePhotos.length > 0 && (
              <div className="photo-preview">
                {beforePhotos.map(p => (
                  <img key={p.id} src={p.url} alt="Before" className="photo-thumb" />
                ))}
              </div>
            )}
            {beforePhotos.length === 0 ? (
              <button
                onClick={() => handlePhotoUpload('before')}
                disabled={uploading}
                className="btn-photo"
              >
                {uploading ? 'Uploading...' : 'Take Before Photo'}
              </button>
            ) : (
              <button
                onClick={() => handlePhotoUpload('after')}
                disabled={uploading}
                className="btn-photo"
              >
                {uploading ? 'Uploading...' : 'Take After Photo'}
              </button>
            )}
          </div>
        )}

        {item.status === 'in_progress' && item.verification_type === 'timer' && (
          <ReadingTimer
            minutes={timerMinutes}
            active={timerActive}
            onStart={() => setTimerActive(true)}
            onComplete={handleTimerComplete}
          />
        )}

        {/* Returned: show resubmit option */}
        {item.status === 'returned' && (
          <button onClick={handleResubmit} className="btn-resubmit">
            Try Again
          </button>
        )}

        {/* Approved: show checkmark */}
        {item.status === 'approved' && (
          <div className="checklist-item-approved">Approved</div>
        )}

        {/* Pending states: show waiting indicator */}
        {item.status === 'pending_ai' && (
          <div className="checklist-item-pending">AI is checking your work...</div>
        )}

        {item.status === 'pending_parent' && (
          <div className="checklist-item-pending">Waiting for Dad's approval</div>
        )}
      </div>

      {/* Show photos for items in review */}
      {['pending_ai', 'pending_parent', 'approved'].includes(item.status) && (item.photos || []).length > 0 && (
        <div className="checklist-item-photos">
          {beforePhotos.map(p => (
            <div key={p.id} className="photo-container">
              <span className="photo-label">Before</span>
              <img src={p.url} alt="Before" className="photo-thumb" />
            </div>
          ))}
          {afterPhotos.map(p => (
            <div key={p.id} className="photo-container">
              <span className="photo-label">After</span>
              <img src={p.url} alt="After" className="photo-thumb" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
